import {
  ActionIcon,
  Anchor,
  Badge,
  Center,
  Group,
  Loader,
  Paper,
  ScrollArea,
  Skeleton,
  Stack,
  Table,
  Text,
  Tooltip,
} from '@mantine/core';
import dayjs from 'dayjs';
import { useState } from 'react';
import { BiError } from 'react-icons/bi';
import {
  FaDollarSign,
  FaDownload,
  FaEye,
  FaSort,
  FaSortDown,
  FaSortUp,
} from 'react-icons/fa';
import { PiWarningThin } from 'react-icons/pi';
import { Link } from 'wouter';
import { MONDAY_BOARDS, mondayItemUrl } from '@/lib/monday';
import {
  InvoiceStatus,
  TInvoice,
  TInvoiceOpportunityStatus,
} from '../../types';
import { getStatusLabel } from '../StatusBadge';
import { TestBadge } from '@/components/TestBadge';

interface InvoiceTableProps {
  invoices: TInvoice[];
  loading?: boolean;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  onSort?: (field: string) => void;
  onViewDetails?: (invoice: TInvoice) => void;
  onDownloadPDF?: (invoice: TInvoice) => void;
  emptyMessage?: string;
}

interface InvoiceRowProps {
  invoice: TInvoice;
  onViewDetails?: (invoice: TInvoice) => void;
  onDownloadPDF?: (invoice: TInvoice) => void;
  formatCurrency: (amount: number, currency: string) => string;
  formatDate: (dateString: string) => string;
}

interface SortableHeaderProps {
  field: string;
  label: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  onSort?: (field: string) => void;
}

function InvoiceOpportunityStatus({ invoice }: { invoice: TInvoice }) {
  if (invoice.opportunityStatus === TInvoiceOpportunityStatus.Queued) {
    return (
      <Tooltip label="Opportunity is queued">
        <PiWarningThin />
      </Tooltip>
    );
  }

  if (invoice.opportunityStatus === TInvoiceOpportunityStatus.Processing) {
    return (
      <Tooltip label="Opportunity is creating">
        <Loader size={12} />
      </Tooltip>
    );
  }

  if (invoice.opportunityStatus === TInvoiceOpportunityStatus.Failed) {
    return (
      <Tooltip label="Opportunity creation failed. Please contact support.">
        <BiError size={16} color="red" />
      </Tooltip>
    );
  }

  if (invoice.opportunityStatus === TInvoiceOpportunityStatus.Created) {
    return (
      <Tooltip label="Opportunity created.">
        <ActionIcon
          variant="light"
          size="sm"
          onClick={() =>
            window.open(
              mondayItemUrl(
                MONDAY_BOARDS.OPPORTUNITIES,
                invoice.opportunityId!,
              ),
              '_blank',
            )
          }
        >
          <FaDollarSign size={12} />
        </ActionIcon>
      </Tooltip>
    );
  }
}

function SortableHeader({
  field,
  label,
  sortBy,
  sortOrder,
  onSort,
}: SortableHeaderProps) {
  const isActive = sortBy === field;

  const getSortIcon = () => {
    if (!isActive) return <FaSort size={12} />;
    return sortOrder === 'asc' ? (
      <FaSortUp size={12} />
    ) : (
      <FaSortDown size={12} />
    );
  };

  return (
    <Group
      gap="xs"
      style={{ cursor: onSort ? 'pointer' : 'default' }}
      onClick={() => onSort?.(field)}
      wrap="nowrap"
    >
      <Text fz="xs" fw={500}>
        {label}
      </Text>
      {onSort && getSortIcon()}
    </Group>
  );
}

function LoadingRow() {
  return (
    <Table.Tr>
      <Table.Td>
        <Skeleton height={16} />
      </Table.Td>
      <Table.Td>
        <Skeleton height={16} />
      </Table.Td>
      <Table.Td>
        <Skeleton height={16} />
      </Table.Td>
      <Table.Td>
        <Skeleton height={16} />
      </Table.Td>
      <Table.Td>
        <Skeleton height={16} />
      </Table.Td>
      <Table.Td>
        <Skeleton height={16} />
      </Table.Td>
      <Table.Td>
        <Skeleton height={16} />
      </Table.Td>
      <Table.Td>
        <Skeleton height={16} />
      </Table.Td>
      <Table.Td>
        <Skeleton height={16} />
      </Table.Td>
      <Table.Td>
        <Skeleton height={16} />
      </Table.Td>
      <Table.Td>
        <Skeleton height={16} />
      </Table.Td>
    </Table.Tr>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <Table.Tr>
      <Table.Td colSpan={11}>
        <Center p="xl">
          <Text c="dimmed" size="md">
            {message}
          </Text>
        </Center>
      </Table.Td>
    </Table.Tr>
  );
}

function InvoiceRow({
  invoice,
  onViewDetails,
  onDownloadPDF,
  formatCurrency,
  formatDate,
}: InvoiceRowProps) {
  const [isDownloading, setIsDownloading] = useState(false);
  const splitActive = invoice.partNumber != null;
  const isCancelled = invoice.status === 'cancelled';

  const handleDownloadPDF = async () => {
    if (isDownloading || !onDownloadPDF) return;

    setIsDownloading(true);
    try {
      onDownloadPDF(invoice);
    } finally {
      setTimeout(() => {
        setIsDownloading(false);
      }, 3000);
    }
  };

  return (
    <Table.Tr>
      <Table.Td>
        <Text fz="xs">{formatDate(invoice.createdAt)}</Text>
      </Table.Td>
      <Table.Td>
        <Stack gap={2}>
          <Text fz="xs" fw={500}>
            {invoice.contactName}
          </Text>
          <Text fz="xs" c="dimmed">
            {invoice.contactEmail}
          </Text>
        </Stack>
      </Table.Td>
      <Table.Td>
        <Group gap={6} wrap="nowrap">
          <Text fz="xs" fw={500}>
            {invoice.invoiceNumber}
          </Text>
          {invoice.isTest && <TestBadge size="xs" />}
        </Group>
      </Table.Td>
      <Table.Td>
        {invoice.offerId ? (
          <Stack gap={2}>
            <Anchor
              component={Link}
              href={`~/offers/${invoice.offerId}/overview`}
              fz="xs"
              fw={500}
              lineClamp={1}
              maw={180}
            >
              {invoice.proposalName || `Offer #${invoice.offerId}`}
            </Anchor>
            {splitActive && (
              <Tooltip
                label={
                  isCancelled
                    ? 'Superseded by a committed split — installments managed on the offer Payments tab'
                    : 'Offer has a committed split payment schedule'
                }
              >
                <Badge
                  size="xs"
                  variant="light"
                  color={isCancelled ? 'red' : 'indigo'}
                >
                  {isCancelled ? 'Cancelled — split' : 'Split'}
                </Badge>
              </Tooltip>
            )}
          </Stack>
        ) : (
          <Text fz="xs" c="dimmed">
            Manual
          </Text>
        )}
      </Table.Td>
      <Table.Td>
        <Text fz="xs" fw={500}>
          {invoice.paymentMethod}
        </Text>
      </Table.Td>
      <Table.Td>
        <Text fz="xs">
          {formatCurrency(invoice.dueAmount, invoice.dueCurrency)}
        </Text>
      </Table.Td>
      <Table.Td>
        <Text fz="xs">
          {formatCurrency(invoice.paidAmount, invoice.dueCurrency)}
        </Text>
      </Table.Td>
      <Table.Td>
        <Text
          fz="xs"
          c={
            // Only what is still collectable is worth flagging. A settled invoice has
            // nothing outstanding whatever it was settled for, so a short settlement is
            // not a debt to chase in red.
            invoice.status !== InvoiceStatus.PAID &&
            invoice.remainingAmount > invoice.dueAmount * 0.1
              ? 'red'
              : undefined
          }
        >
          {formatCurrency(invoice.remainingAmount, invoice.dueCurrency)}
        </Text>
      </Table.Td>
      <Table.Td>
        <Text fz="xs">{invoice.dueAt ? formatDate(invoice.dueAt) : '-'}</Text>
      </Table.Td>
      <Table.Td>
        <Text fz="xs" c={invoice.partNumber != null ? 'indigo' : 'dimmed'}>
          {invoice.partNumber != null ? invoice.partNumber : '—'}
        </Text>
      </Table.Td>
      <Table.Td>
        <Group gap="xs">
          <Text fz="xs">{getStatusLabel(invoice.status)}</Text>
        </Group>
      </Table.Td>
      <Table.Td>
        <Group gap="xs" wrap="nowrap">
          <InvoiceOpportunityStatus invoice={invoice} />
          {onViewDetails && (
            <ActionIcon
              variant="light"
              size="sm"
              onClick={() => onViewDetails(invoice)}
              title="View Details"
            >
              <FaEye size={12} />
            </ActionIcon>
          )}
          {onDownloadPDF && (
            <ActionIcon
              variant="light"
              size="sm"
              onClick={handleDownloadPDF}
              title="Download PDF"
              loading={isDownloading}
            >
              <FaDownload size={12} />
            </ActionIcon>
          )}
        </Group>
      </Table.Td>
    </Table.Tr>
  );
}

export function InvoiceTable({
  invoices,
  loading = false,
  sortBy,
  sortOrder,
  onSort,
  onViewDetails,
  onDownloadPDF,
  emptyMessage = 'No invoices found',
}: InvoiceTableProps) {
  const formatCurrency = (amount: number, currency: string) => {
    return `${amount.toFixed(2)} ${currency}`;
  };

  const formatDate = (dateString: string) => {
    return dayjs(dateString).format('DD.MM.YYYY');
  };

  return (
    <Paper withBorder>
      <ScrollArea>
        <Table
          striped
          highlightOnHover
          styles={{
            td: {
              whiteSpace: 'nowrap',
            },
            th: {
              whiteSpace: 'nowrap',
            },
          }}
        >
          <Table.Thead>
            <Table.Tr>
              <Table.Th>
                <SortableHeader
                  field="created_at"
                  label="Date Created"
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onSort={onSort}
                />
              </Table.Th>
              <Table.Th>
                <Text fz="xs" fw={500}>
                  Contact
                </Text>
              </Table.Th>
              <Table.Th>
                <Text fz="xs" fw={500}>
                  Invoice Number
                </Text>
              </Table.Th>
              <Table.Th>
                <Text fz="xs" fw={500}>
                  Offer
                </Text>
              </Table.Th>
              <Table.Th>
                <Text fz="xs" fw={500}>
                  Payment Method
                </Text>
              </Table.Th>
              <Table.Th>
                <SortableHeader
                  field="total_amount"
                  label="Invoice Amount"
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onSort={onSort}
                />
              </Table.Th>
              <Table.Th>
                <Text fz="xs" fw={500}>
                  Amount Paid
                </Text>
              </Table.Th>
              <Table.Th>
                <Text fz="xs" fw={500}>
                  Remaining
                </Text>
              </Table.Th>
              <Table.Th>
                <SortableHeader
                  field="due_at"
                  label="Due Date"
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onSort={onSort}
                />
              </Table.Th>
              <Table.Th>
                <Text fz="xs" fw={500}>
                  Part #
                </Text>
              </Table.Th>
              <Table.Th>
                <SortableHeader
                  field="status"
                  label="Status"
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onSort={onSort}
                />
              </Table.Th>
              <Table.Th>
                <Text fz="xs" fw={500}>
                  Actions
                </Text>
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {loading ? (
              <>
                <LoadingRow />
                <LoadingRow />
                <LoadingRow />
              </>
            ) : invoices.length === 0 ? (
              <EmptyState message={emptyMessage} />
            ) : (
              invoices.map((invoice) => (
                <InvoiceRow
                  key={invoice.id}
                  invoice={invoice}
                  onViewDetails={onViewDetails}
                  onDownloadPDF={onDownloadPDF}
                  formatCurrency={formatCurrency}
                  formatDate={formatDate}
                />
              ))
            )}
          </Table.Tbody>
        </Table>
      </ScrollArea>
    </Paper>
  );
}

export default InvoiceTable;
